/**
 * Favoris (wishlist) — stockage local, sans compte client ni application.
 *
 * Constat de départ : les trois cœurs du thème (carte produit, fiche produit,
 * en-tête) étaient purement décoratifs. Aucun JavaScript, aucun localStorage,
 * aucune application : cliquer ne faisait rien, les classes .active / .is-active
 * n'étaient jamais posées, et /pages/wishlist n'existait pas.
 *
 * Ce fichier fournit :
 *   - le stockage (localStorage, clé `minuit12:wishlist`) ;
 *   - la bascule au clic, en délégation d'évènement (fonctionne donc aussi sur
 *     les cartes injectées après coup : filtres, pagination, recherche) ;
 *   - la synchronisation de l'état visuel de tous les cœurs de la page ;
 *   - le compteur de l'en-tête ;
 *   - l'évènement `wishlist:change`, utilisé par la page Favoris pour se
 *     redessiner, et la synchronisation entre onglets.
 *
 * L'API est exposée en `window.MinuitWishlist`.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'minuit12:wishlist';

  /* --------------------------------------------------------------- stockage */

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // On normalise en chaînes : les id viennent tantôt d'un attribut HTML
      // (chaîne), tantôt d'un JSON (nombre).
      return parsed.map(String).filter(Boolean);
    } catch (e) {
      // Navigation privée, quota dépassé, JSON corrompu : on dégrade
      // silencieusement plutôt que de casser la page.
      return [];
    }
  }

  function write(ids) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (e) {
      /* stockage indisponible : la session reste fonctionnelle, sans mémoire */
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { ids: ids } }));
  }

  function has(id) {
    return read().indexOf(String(id)) !== -1;
  }

  function toggle(id) {
    id = String(id);
    var ids = read();
    var i = ids.indexOf(id);
    if (i === -1) ids.push(id);
    else ids.splice(i, 1);
    write(ids);
    return i === -1; // true si le produit vient d'être ajouté
  }

  function remove(id) {
    var ids = read().filter(function (x) {
      return x !== String(id);
    });
    write(ids);
  }

  /* ------------------------------------------------------------------- vue */

  /** Les deux implémentations de cœur du thème, avec leur classe d'état. */
  var BUTTONS = [
    { selector: '.wishlist-btn', activeClass: 'active' },
    { selector: '.product__wishlist-heart', activeClass: 'is-active' },
  ];

  function syncButtons() {
    var ids = read();
    BUTTONS.forEach(function (kind) {
      document.querySelectorAll(kind.selector + '[data-product-id]').forEach(function (btn) {
        var on = ids.indexOf(String(btn.dataset.productId)) !== -1;
        btn.classList.toggle(kind.activeClass, on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    });
  }

  function syncCount() {
    var n = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n;
      // La pastille reste masquée quand la liste est vide, comme celle du panier.
      el.hidden = n === 0;
    });
  }

  function syncAll() {
    syncButtons();
    syncCount();
  }

  /* -------------------------------------------------------------- écouteurs */

  document.addEventListener('click', function (event) {
    var target = event.target.closest
      ? event.target.closest('.wishlist-btn, .product__wishlist-heart')
      : null;
    if (!target || !target.dataset.productId) return;

    // Le cœur de la carte produit est posé au-dessus du lien qui couvre toute
    // la carte : sans cela, cliquer sur le cœur naviguerait vers le produit.
    event.preventDefault();
    event.stopPropagation();

    toggle(target.dataset.productId);
    syncAll();
  });

  // Retrait depuis la page Favoris
  document.addEventListener('click', function (event) {
    var btn = event.target.closest ? event.target.closest('[data-wishlist-remove]') : null;
    if (!btn) return;
    event.preventDefault();
    remove(btn.dataset.wishlistRemove);
  });

  // Onglets multiples : garder les listes cohérentes
  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY) syncAll();
  });

  document.addEventListener('wishlist:change', syncCount);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }

  window.MinuitWishlist = {
    get: read,
    has: has,
    toggle: toggle,
    remove: remove,
    sync: syncAll,
    STORAGE_KEY: STORAGE_KEY,
  };
})();
